<template>
    <section
        :class="embedded ? 'iod-root iod-root--embedded w-full text-left' : 'iod-root min-h-screen w-full flex flex-col bg-gradient-to-br from-gray-100 to-blue-50 text-left'">
        <div :class="embedded ? 'w-full' : 'h-full container mx-auto'">
            <div :class="embedded ? 'flex flex-col w-full px-2 py-2' : 'flex flex-col h-full w-full p-6 md:p-8'">
                <nav v-if="!embedded" class="text-sm text-gray-600 mb-6 flex flex-wrap items-center">
                    <router-link to="/dashboard"
                        class="hover:text-blue-700 transition font-medium">Dashboard</router-link>
                    <span class="mx-1">/</span>
                    <router-link to="/training-module"
                        class="hover:text-blue-700 transition font-medium">Internship</router-link>
                    <span class="mx-1">/</span>
                    <router-link to="/internship-clinical-placement"
                        class="hover:text-blue-700 transition font-medium">Opportunities</router-link>
                    <span class="mx-1">/</span>
                    <span class="text-blue-900 font-semibold">Details</span>
                </nav>

                <div v-if="pageLoading" class="iod-panel iod-panel-muted">
                    <p class="text-gray-600 text-sm">Loading opportunity…</p>
                </div>

                <div v-else-if="loadError" class="iod-panel iod-panel-error">
                    <p class="text-gray-800 font-semibold">{{ loadError }}</p>
                    <router-link v-if="!embedded" to="/internship-clinical-placement"
                        class="iod-back-link iod-back-link-inline">
                        Back to opportunities
                    </router-link>
                </div>

                <div v-else-if="row" class="w-full">
                    <router-link v-if="!embedded" to="/internship-clinical-placement" class="iod-back-link">
                        ← Back to opportunities
                    </router-link>

                    <div class="iod-hero">
                        <div class="iod-hero-stripe" aria-hidden="true"></div>
                        <div class="iod-hero-inner">
                            <div class="iod-hero-top">
                                <div class="iod-hero-leading">
                                    <span class="iod-icon-tile" aria-hidden="true">
                                        <svg class="iod-icon-svg" viewBox="0 0 24 24" fill="none"
                                            xmlns="http://www.w3.org/2000/svg">
                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.65"
                                                stroke="currentColor"
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        </svg>
                                    </span>
                                    <span v-if="row.opportunity_type" class="iod-chip iod-chip-type">
                                        {{ formatOpportunityType(row.opportunity_type) }}
                                    </span>
                                </div>
                                <span class="iod-chip iod-chip-entity">{{ formatEntityName(row.entity) }}</span>
                            </div>
                            <h1 class="iod-title">{{ row.title }}</h1>
                        </div>
                    </div>

                    <div class="iod-panel iod-panel-body">
                        <h2 class="iod-section-title">Overview</h2>
                        <dl class="iod-dl">
                            <div class="iod-dl-row">
                                <dt class="iod-dt">Entity</dt>
                                <dd class="iod-dd">{{ entityLabel }}</dd>
                            </div>
                            <div class="iod-dl-row">
                                <dt class="iod-dt">Campus</dt>
                                <dd class="iod-dd">{{ campusLabel }}</dd>
                            </div>
                            <div class="iod-dl-row">
                                <dt class="iod-dt">Capacity</dt>
                                <dd class="iod-dd">
                                    <span v-if="row.student_count === null">Unlimited seats</span>
                                    <span v-else>{{ row.student_count }} seats</span>
                                </dd>
                            </div>
                            <div v-if="showListingPeriodInOverview" class="iod-dl-row">
                                <dt class="iod-dt">Listed for students</dt>
                                <dd class="iod-dd">{{ formatListingPeriod(row) }}</dd>
                            </div>
                            <div class="iod-dl-row">
                                <dt class="iod-dt">Posted</dt>
                                <dd class="iod-dd">{{ formatPostedDate(row.created_at) }}</dd>
                            </div>
                            <div class="iod-dl-row">
                                <dt class="iod-dt">Created by</dt>
                                <dd class="iod-dd">{{ formatCreatedBy(row) }}</dd>
                            </div>
                        </dl>

                        <h2 class="iod-section-title iod-section-title-spaced">Description</h2>
                        <div class="iod-desc-panel">
                            <p v-if="row.description" class="iod-desc-text">{{ row.description }}</p>
                            <p v-else class="iod-desc-text iod-desc-muted">No description has been provided.</p>
                        </div>

                        <div v-if="showStudentEnrollUi && row" class="iod-enroll-wrap">
                            <h2 class="iod-section-title iod-section-title-spaced">Enrollment</h2>
                            <div class="iod-enroll-bar"
                                :class="canResubmitEnrollment ? 'iod-enroll-bar--rejected' : ''">
                                <template v-if="row.viewer_has_enrolled">
                                    <span class="iod-chip"
                                        :class="enrollmentStatusChipClass(row.viewer_enrollment_status)">
                                        {{ formatEnrollmentStatus(row.viewer_enrollment_status) }}
                                    </span>
                                    <template v-if="canResubmitEnrollment">
                                        <button type="button" class="iod-btn-resubmit"
                                            :disabled="resubmitSubmitting" @click="onResubmit">
                                            {{ resubmitSubmitting ? 'Resubmitting…' : 'Resubmit request' }}
                                        </button>
                                    </template>
                                </template>
                                <button v-else type="button" class="iod-btn-enroll" :disabled="enrollSubmitting"
                                    @click="onEnroll">
                                    {{ enrollSubmitting ? 'Enrolling…' : 'Enroll' }}
                                </button>
                            </div>
                            <p v-if="canResubmitEnrollment" class="iod-resubmit-hint">
                                Your request was rejected. Please read the staff message below, complete any required
                                actions, then resubmit so staff can review it again.
                            </p>
                            <div v-if="row.viewer_has_enrolled && row.viewer_enrollment_id && canViewEnrollmentConversation"
                                class="mt-4">
                                <label class="text-xs text-gray-500 font-medium mb-2 block">
                                    Conversation with {{ opportunityCreatorName }}
                                </label>
                                <enrollment-chat
                                    :key="'enrollment-chat-' + row.viewer_enrollment_id + '-' + chatRefreshKey"
                                    :enrollment-id="row.viewer_enrollment_id"
                                    student-name="You"
                                    :can-reply="canReplyEnrollmentConversation"
                                />
                            </div>
                        </div>

                        <div v-if="showEnrolledStudentsSection && row" class="iod-enrolled-wrap">
                            <h2 class="iod-section-title iod-section-title-spaced">Enrolled students</h2>
                            <div v-if="enrolledStudentsLoading" class="iod-enrolled-loading">
                                <base-page-spinner type="spin" class-list="h-10"></base-page-spinner>
                            </div>
                            <div v-else-if="enrolledStudents.length" class="iod-enrolled-table-wrap">
                                <div class="overflow-x-auto">
                                    <table class="min-w-full text-left text-sm">
                                        <thead class="border-b">
                                            <tr>
                                                <th class="py-2 px-3 font-semibold text-gray-500">#</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Student ID</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Name</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Emirates ID</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Entity</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Campus</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Program</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Submitted</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Status</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Source</th>
                                                <th class="py-2 px-3 font-semibold text-gray-500">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="(student, idx) in enrolledStudents"
                                                :key="student.enrollment_id ? student.enrollment_id : idx"
                                                class="border-b border-dashed hover:bg-gray-50">
                                                <td class="py-2 px-3 text-gray-900">
                                                    {{ enrolledStudentsPaginatorInfo.firstItem + idx }}
                                                </td>
                                                <td class="py-2 px-3 text-gray-900">
                                                    <span v-if="student.student_id"
                                                        class="text-heading hover:text-blue-600 hover:underline cursor-pointer"
                                                        @click.stop="viewStudentProfile(student.student_id)">{{
                                                        student.student_id }}</span>
                                                    <span v-else>—</span>
                                                </td>
                                                <td class="py-2 px-3 text-gray-900">
                                                    {{ student.name_en ? student.name_en : '—' }}
                                                </td>
                                                <td class="py-2 px-3 text-gray-900">
                                                    {{ student.eid ? student.eid : '—' }}
                                                </td>
                                                <td class="py-2 px-3 text-gray-900">
                                                    {{ formatEntityName(student.entity) }}
                                                </td>
                                                <td class="py-2 px-3 text-gray-900">
                                                    {{ student.campus_code ? student.campus_code : '—' }}
                                                </td>
                                                <td class="py-2 px-3 text-gray-900">
                                                    {{ student.program_name ? student.program_name : '—' }}
                                                </td>
                                                <td class="py-2 px-3 text-gray-900 whitespace-nowrap">
                                                    {{ formatEnrolledAt(student.submitted_at || student.enrolled_at) }}
                                                </td>
                                                <td class="py-2 px-3">
                                                    <span class="iod-status-pill"
                                                        :class="enrollmentStatusClass(student.status)">
                                                        {{ formatEnrollmentStatus(student.status) }}
                                                    </span>
                                                </td>
                                                <td class="py-2 px-3">
                                                    <span class="iod-source-pill"
                                                        :class="assignmentSourceClass(student.assignment_source)">
                                                        {{ formatAssignmentSource(student.assignment_source) }}
                                                    </span>
                                                </td>
                                                <td class="py-2 px-3 whitespace-nowrap">
                                                    <div class="flex flex-wrap items-center gap-2">
                                                        <template v-if="canReviewEnrollments">
                                                            <button v-if="student.status === 'pending'" type="button"
                                                                class="iod-btn-action iod-btn-action--approve"
                                                                :disabled="reviewSubmitting"
                                                                @click="approveEnrollment(student)">
                                                                Approve
                                                            </button>
                                                            <button v-if="student.status === 'pending'" type="button"
                                                                class="iod-btn-action iod-btn-action--reject"
                                                                :disabled="reviewSubmitting"
                                                                @click="rejectEnrollment(student)">
                                                                Reject
                                                            </button>
                                                        </template>
                                                        <button type="button" class="icl-history-btn"
                                                            @click="openEnrollmentHistory(student)">
                                                            History
                                                        </button>
                                                        <button v-if="canViewEnrollmentConversation" type="button"
                                                            class="icl-chat-btn"
                                                            @click="openEnrollmentChat(student)">
                                                            <span class="icl-chat-btn-icon-wrap">
                                                                <font-awesome-icon :icon="['fas', 'comments']"
                                                                    class="icl-chat-btn-icon" />
                                                            </span>
                                                            <span class="icl-chat-btn-label">
                                                                {{ canReplyEnrollmentConversation ? 'View & Reply' : 'View Conversation' }}
                                                            </span>
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                                <div v-if="enrolledStudentsPaginatorInfo && enrolledStudentsPaginatorInfo.lastPage > 1"
                                    class="mt-4">
                                    <pagination :paginatorInfo="enrolledStudentsPaginatorInfo"
                                        :setPage="setEnrolledStudentsPage"></pagination>
                                </div>
                            </div>
                            <div v-else class="iod-enrolled-empty">
                                No students have enrolled in this opportunity yet.
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <transition name="icl-fade">
            <base-modal v-if="chatEnrollment" width="600px" @close="closeEnrollmentChat">
                <template #header>
                    <div class="icl-modal-header">
                        <div class="icl-modal-header-inner">
                            <div class="icl-modal-header-icon-wrap">
                                <font-awesome-icon :icon="['fas', 'comments']" class="icl-modal-header-icon" />
                            </div>
                            <div class="text-left">
                                <h2 class="icl-modal-header-title">Conversation</h2>
                                <p class="icl-modal-header-sub">
                                    {{ chatEnrollment.name_en ? chatEnrollment.name_en : 'Student' }}
                                    <span v-if="chatEnrollment.student_id" class="icl-modal-header-meta">
                                        · {{ chatEnrollment.student_id }}
                                    </span>
                                    <span v-if="chatEnrollment.eid" class="icl-modal-header-meta">
                                        · {{ chatEnrollment.eid }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="icl-modal-body">
                    <div class="icl-modal-card">
                        <enrollment-chat variant="modal" :enrollment-id="chatEnrollment.enrollment_id"
                            :student-name="chatEnrollment.name_en ? chatEnrollment.name_en : 'Student'"
                            :can-reply="canReplyEnrollmentConversation" />
                    </div>
                    <p v-if="canReplyEnrollmentConversation" class="icl-modal-hint">
                        <font-awesome-icon :icon="['fas', 'lightbulb']" class="icl-modal-hint-icon" />
                        Messages are shared with the student. They will be notified when you reply.
                    </p>
                </div>
            </base-modal>
        </transition>

        <transition name="icl-fade">
            <base-modal
                v-if="historyEnrollment"
                width="560px"
                height="80vh"
                @close="closeEnrollmentHistory"
            >
                <template #header>
                    <div class="icl-modal-header">
                        <div class="icl-modal-header-inner">
                            <div class="icl-modal-header-icon-wrap icl-modal-header-icon-wrap--history">
                                <font-awesome-icon :icon="['fas', 'clock-rotate-left']" class="icl-modal-header-icon" />
                            </div>
                            <div class="text-left">
                                <h2 class="icl-modal-header-title">Enrollment history</h2>
                                <p class="icl-modal-header-sub">
                                    {{ historyEnrollment.name_en ? historyEnrollment.name_en : 'Student' }}
                                    <span v-if="historyEnrollment.student_id" class="icl-modal-header-meta">
                                        · {{ historyEnrollment.student_id }}
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </template>

                <div class="icl-modal-body">
                    <div class="iod-history-timeline">
                        <div
                            v-for="item in buildEnrollmentHistory(historyEnrollment)"
                            :key="item.key"
                            class="iod-history-item"
                        >
                            <div
                                class="iod-history-item-dot"
                                :class="enrollmentEventTypeClass(item.event_type)"
                            ></div>
                            <div class="iod-history-item-body">
                                <div class="iod-history-item-top">
                                    <span
                                        class="iod-history-type"
                                        :class="enrollmentEventTypeClass(item.event_type)"
                                    >
                                        {{ formatEnrollmentEventType(item.event_type) }}
                                    </span>
                                    <span class="iod-history-time">{{ formatEnrolledAt(item.created_at) }}</span>
                                </div>
                                <p v-if="item.actor" class="iod-history-actor">{{ item.actor }}</p>
                                <p v-if="item.note" class="iod-history-note">{{ item.note }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </base-modal>
        </transition>

        <transition name="fade" mode="out-in">
            <base-full-modal v-if="showStudentProfileFullModal" title="Student Profile"
                @close="closeStudentProfileModal">
                <student-profile :student-id="studentId"></student-profile>
            </base-full-modal>
        </transition>
    </section>
</template>

<script>
import gql from "graphql-tag";
import Pagination from "../../components/shared/pagination.vue";
import EnrollmentChat from "../../components/internship-clinical-placement/EnrollmentChat.vue";
import StudentProfile from "../student-profile/index.vue";
import {
    formatEntityName,
    formatOpportunityType,
    formattedDateWithTimeAmPm,
    getEntityName,
} from "../../helpers/utils";

export default {
    name: "internship-clinical-placement-opportunity-detail",
    components: {
        Pagination,
        EnrollmentChat,
        StudentProfile,
    },
    props: {
        token: {
            type: String,
            default: "",
        },
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            pageLoading: true,
            loadError: "",
            row: null,
            enrollSubmitting: false,
            enrolledStudentsLoading: false,
            enrolledStudents: [],
            enrolledStudentsPaginatorInfo: null,
            enrolledStudentsPage: 1,
            enrolledStudentsFirst: 10,
            chatEnrollment: null,
            historyEnrollment: null,
            reviewSubmitting: false,
            resubmitSubmitting: false,
            chatRefreshKey: 0,
            showStudentProfileFullModal: false,
            studentId: null,
        };
    },
    computed: {
        studentStatus() {
            return this.$store.getters.studentStatus || "";
        },
        effectiveToken() {
            const fromProp = this.token && this.token.length ? this.token : "";
            if (fromProp) {
                return fromProp;
            }
            const route = this.$route;
            const p = route && route.params && route.params.token ? route.params.token : "";
            return p ? String(p) : "";
        },
        campusLabel() {
            const r = this.row;
            if (!r || !r.campus_code) {
                return "—";
            }
            const s = this.formatCampuses(r.campus_code);
            return s && s.trim() ? s.trim() : "—";
        },
        entityLabel() {
            const r = this.row;
            if (!r || !r.entity) {
                return "—";
            }
            return formatEntityName(r.entity);
        },
        showListingPeriodInOverview() {
            const role = this.$store.getters.role || "";
            return role !== "user";
        },
        showStudentEnrollUi() {
            const role = this.$store.getters.role || "";
            if (role !== "user") {
                return false;
            }
            const isSuperAdmin = this.$store.getters.isSuperAdmin;
            if (!this.hasPermission("internship") && !this.hasPermission("internship-index") && !isSuperAdmin) {
                return false;
            }
            if (this.studentStatus !== "AS") {
                return false;
            }
            return true;
        },
        showEnrolledStudentsSection() {
            const role = this.$store.getters.role || "";
            if (role === "user") {
                return false;
            }
            return this.hasPermission("view-internship-enrolled-users");
        },
        canViewEnrollmentConversation() {
            return this.hasPermission("view-internship-enrollment-conversation");
        },
        canReplyEnrollmentConversation() {
            return this.canViewEnrollmentConversation
                && this.hasPermission("reply-internship-enrollment-conversation");
        },
        canReviewEnrollments() {
            if (!this.showEnrolledStudentsSection || !this.row) {
                return false;
            }
            if (!this.hasPermission("internship-approval")) {
                return false;
            }
            const userId = this.$store.getters.userId;
            const creator = this.row.created_by_user;
            const creatorId = creator && creator.id ? creator.id : null;
            if (!userId || !creatorId) {
                return false;
            }
            return String(userId) === String(creatorId);
        },
        canResubmitEnrollment() {
            if (!this.showStudentEnrollUi || !this.row || !this.row.viewer_has_enrolled) {
                return false;
            }
            if (!this.row.viewer_enrollment_id) {
                return false;
            }
            return this.row.viewer_enrollment_status === "rejected";
        },
        opportunityCreatorName() {
            const u = this.row && this.row.created_by_user ? this.row.created_by_user : null;
            if (u && u.name) {
                const name = String(u.name).trim();
                if (name) {
                    return name;
                }
            }
            return "staff";
        },
    },
    watch: {
        effectiveToken() {
            this.ensurePermissionAndFetch();
        },
    },
    mounted() {
        this.ensurePermissionAndFetch();
    },
    methods: {
        formatEntityName,
        formatOpportunityType,
        getEntityName,
        formatCampuses(value) {
            if (!value) return "";
            if (Array.isArray(value)) {
                return value.join(", ");
            }
            return String(value);
        },
        formatPostedDate(value) {
            if (!value) {
                return "—";
            }
            const s = String(value).trim();
            if (s.length >= 10 && s.charAt(4) === "-" && s.charAt(7) === "-") {
                const y = s.substring(0, 4);
                const m = s.substring(5, 7);
                const d = s.substring(8, 10);
                return d + "-" + m + "-" + y;
            }
            const dt = new Date(s);
            if (!isNaN(dt.getTime())) {
                const day = dt.getDate();
                const month = dt.getMonth() + 1;
                const year = dt.getFullYear();
                const dd = day < 10 ? "0" + day : String(day);
                const mm = month < 10 ? "0" + month : String(month);
                return dd + "-" + mm + "-" + String(year);
            }
            return s;
        },
        formatCreatedBy(item) {
            const u = item && item.created_by_user ? item.created_by_user : null;
            if (!u || !u.name) {
                return "Unknown";
            }
            const name = String(u.name).trim();
            return name ? name : "Unknown";
        },
        formatListingPeriod(item) {
            if (!item) {
                return "—";
            }
            const start = item.start_date ? this.formatPostedDate(item.start_date) : "";
            const end = item.end_date ? this.formatPostedDate(item.end_date) : "";
            if (start && end) {
                return "Visible from " + start + " to " + end;
            }
            if (start) {
                return "Visible from " + start;
            }
            if (end) {
                return "Visible until " + end;
            }
            return "—";
        },
        hasPermission(name) {
            const permissions = this.$store.getters.permissions || "";
            const permissionsArray = permissions.split(",");
            return permissionsArray.includes(name);
        },
        ensurePermissionAndFetch() {
            const role = this.$store.getters.role || "";
            const isSuperAdmin = this.$store.getters.isSuperAdmin;

            if (!this.effectiveToken) {
                if (this.embedded) {
                    this.pageLoading = false;
                    this.loadError = "Opportunity reference is missing.";
                    return;
                }
                this.$router.replace("/internship-clinical-placement");
                return;
            }

            if (role === "user") {
                if (!this.hasPermission("internship") && !this.hasPermission("internship-index") && !isSuperAdmin) {
                    if (this.embedded) {
                        this.pageLoading = false;
                        this.loadError = "You do not have permission to view this opportunity.";
                        return;
                    }
                    this.$router.replace("/no-permission");
                    return;
                }
                if (!isSuperAdmin && this.studentStatus !== "AS") {
                    if (this.embedded) {
                        this.pageLoading = false;
                        this.loadError = "You do not have permission to view this opportunity.";
                        return;
                    }
                    this.$router.replace("/no-permission");
                    return;
                }
            } else {
                if (!this.hasPermission("internship-index") && !isSuperAdmin) {
                    if (this.embedded) {
                        this.pageLoading = false;
                        this.loadError = "You do not have permission to view this opportunity.";
                        return;
                    }
                    this.$router.replace("/no-permission");
                    return;
                }
            }

            this.fetchOpportunity();
        },
        notifyToast(icon, title) {
            if (this.$swal && typeof this.$swal.mixin === "function") {
                const Toast = this.$swal.mixin({
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3200,
                    timerProgressBar: true,
                });
                Toast.fire({ icon: icon, title: title });
                return;
            }
            window.alert(title);
        },
        async onEnroll() {
            const t = this.effectiveToken;
            if (!t || !this.row || this.row.viewer_has_enrolled || this.enrollSubmitting) {
                return;
            }
            this.enrollSubmitting = true;
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation EnrollInternshipClinicalPlacementOpportunity($token: String!) {
                            enrollInternshipClinicalPlacementOpportunity(token: $token) {
                                error
                                error_code
                                message
                                already_enrolled
                            }
                        }
                    `,
                    variables: { token: t },
                });
                const payload = result && result.data && result.data.enrollInternshipClinicalPlacementOpportunity;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : "Enrollment could not be completed.";
                    this.notifyToast("error", msg);
                    return;
                }
                const msg = payload.message ? payload.message : "Success.";
                if (payload.already_enrolled) {
                    this.notifyToast("info", msg);
                } else {
                    this.notifyToast("success", msg);
                }
                await this.fetchOpportunity(true);
            } catch (e) {
                this.notifyToast("error", "Enrollment failed. Please try again.");
            } finally {
                this.enrollSubmitting = false;
            }
        },
        async onResubmit() {
            if (!this.canResubmitEnrollment || this.resubmitSubmitting) {
                return;
            }
            const enrollmentId = this.row && this.row.viewer_enrollment_id
                ? this.row.viewer_enrollment_id
                : null;
            if (!enrollmentId) {
                return;
            }
            if (!this.$swal) {
                this.notifyToast("error", "Unable to open resubmit dialog.");
                return;
            }
            const swalResult = await this.$swal.fire({
                title: "Resubmit request",
                text: "You can optionally add a message for staff. Leave blank to resubmit without a message.",
                input: "textarea",
                inputPlaceholder: "Optional message for staff",
                inputAttributes: {
                    "aria-label": "Optional message",
                },
                showCancelButton: true,
                confirmButtonText: "Resubmit",
                confirmButtonColor: "#2563eb",
            });
            if (!swalResult.isConfirmed) {
                return;
            }
            const message = swalResult.value ? String(swalResult.value).trim() : "";
            this.resubmitSubmitting = true;
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation ResubmitInternshipClinicalPlacementEnrollment(
                            $enrollmentId: ID!
                            $message: String
                        ) {
                            resubmitInternshipClinicalPlacementEnrollment(
                                enrollment_id: $enrollmentId
                                message: $message
                            ) {
                                error
                                error_code
                                message
                            }
                        }
                    `,
                    variables: {
                        enrollmentId: String(enrollmentId),
                        message: message ? message : null,
                    },
                });
                const payload = result && result.data && result.data.resubmitInternshipClinicalPlacementEnrollment;
                if (!payload || payload.error) {
                    const msg = payload && payload.message
                        ? payload.message
                        : "Could not resubmit the request.";
                    this.notifyToast("error", msg);
                    return;
                }
                this.notifyToast(
                    "success",
                    payload.message || "Request resubmitted successfully."
                );
                this.chatRefreshKey = this.chatRefreshKey + 1;
                await this.fetchOpportunity(true);
            } catch (e) {
                this.notifyToast("error", "Resubmit failed. Please try again.");
            } finally {
                this.resubmitSubmitting = false;
            }
        },
        async fetchOpportunity(silent) {
            const quiet = silent === true;
            if (!quiet) {
                this.pageLoading = true;
                this.loadError = "";
                this.row = null;
            }
            const t = this.effectiveToken;
            if (!t) {
                if (!quiet) {
                    this.pageLoading = false;
                }
                return;
            }
            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query InternshipClinicalPlacementOpportunityByToken($token: String!) {
                            internshipClinicalPlacementOpportunityByToken(token: $token) {
                                error
                                error_code
                                message
                                data {
                                    id
                                    token
                                    entity
                                    campus_code
                                    opportunity_type
                                    title
                                    description
                                    student_count
                                    start_date
                                    end_date
                                    created_at
                                    created_by_user {
                                        id
                                        name
                                    }
                                    viewer_has_enrolled
                                    viewer_enrollment_id
                                    viewer_enrollment_status
                                }
                            }
                        }
                    `,
                    variables: { token: t },
                    fetchPolicy: "network-only",
                });
                const payload = result && result.data && result.data.internshipClinicalPlacementOpportunityByToken;
                if (!payload || payload.error) {
                    if (payload && payload.error_code === 403) {
                        if (this.embedded) {
                            if (!quiet) {
                                this.loadError = "You do not have permission to view this opportunity.";
                            }
                            return;
                        }
                        this.$router.replace("/no-permission");
                        return;
                    }
                    if (payload && payload.error_code === 401) {
                        if (this.embedded) {
                            if (!quiet) {
                                this.loadError = "Your session has expired. Please sign in again.";
                            }
                            return;
                        }
                        this.$router.replace("/");
                        return;
                    }
                    const msg = payload && payload.message ? payload.message : "Unable to load this opportunity.";
                    if (!quiet) {
                        this.loadError = msg;
                    }
                    return;
                }
                const data = payload.data;
                this.row = data ? data : null;
                if (!this.row && !quiet) {
                    this.loadError = "This opportunity could not be found.";
                }
                if (this.row && this.showEnrolledStudentsSection) {
                    this.enrolledStudentsPage = 1;
                    this.fetchEnrolledStudents();
                } else {
                    this.resetEnrolledStudents();
                }
            } catch (e) {
                if (!quiet) {
                    this.loadError = "Something went wrong while loading the opportunity.";
                }
            } finally {
                if (!quiet) {
                    this.pageLoading = false;
                }
            }
        },
        resetEnrolledStudents() {
            this.enrolledStudents = [];
            this.enrolledStudentsPaginatorInfo = null;
            this.enrolledStudentsPage = 1;
        },
        setEnrolledStudentsPage(page) {
            this.enrolledStudentsPage = page;
            this.fetchEnrolledStudents();
        },
        formatEnrolledAt(value) {
            if (!value) {
                return "—";
            }
            const formatted = formattedDateWithTimeAmPm(value);
            return formatted ? formatted : "—";
        },
        enrollmentEvents(student) {
            if (!student || !student.events || !Array.isArray(student.events)) {
                return [];
            }
            return student.events;
        },
        buildEnrollmentHistory(student) {
            if (!student) {
                return [];
            }
            const items = [];
            items.push({
                key: "submitted",
                event_type: "submitted",
                created_at: student.submitted_at || student.enrolled_at,
                note: null,
                actor: student.name_en ? student.name_en : "Student",
            });
            const events = this.enrollmentEvents(student);
            for (var i = 0; i < events.length; i++) {
                const event = events[i];
                const actorName = event && event.user && event.user.name
                    ? String(event.user.name).trim()
                    : "";
                items.push({
                    key: event && event.id ? String(event.id) : ("event-" + i),
                    event_type: event && event.event_type ? event.event_type : "",
                    created_at: event && event.created_at ? event.created_at : null,
                    note: event && event.note ? event.note : null,
                    actor: actorName ? actorName : null,
                });
            }
            return items;
        },
        formatEnrollmentEventType(eventType) {
            const t = eventType ? String(eventType) : "";
            if (t === "submitted") {
                return "Submitted";
            }
            if (t === "rejected") {
                return "Rejected";
            }
            if (t === "resubmitted") {
                return "Resubmitted";
            }
            if (t === "approved") {
                return "Approved";
            }
            return t ? t : "—";
        },
        enrollmentEventTypeClass(eventType) {
            const t = eventType ? String(eventType) : "";
            if (t === "submitted") {
                return "iod-history-type--submitted";
            }
            if (t === "rejected") {
                return "iod-history-type--rejected";
            }
            if (t === "resubmitted") {
                return "iod-history-type--resubmitted";
            }
            if (t === "approved") {
                return "iod-history-type--approved";
            }
            return "";
        },
        openEnrollmentHistory(student) {
            this.historyEnrollment = student ? student : null;
        },
        closeEnrollmentHistory() {
            this.historyEnrollment = null;
        },
        openEnrollmentChat(student) {
            if (!this.canViewEnrollmentConversation) {
                return;
            }
            this.chatEnrollment = student ? student : null;
        },
        closeEnrollmentChat() {
            this.chatEnrollment = null;
        },
        async fetchEnrolledStudents() {
            if (!this.showEnrolledStudentsSection || !this.effectiveToken) {
                this.resetEnrolledStudents();
                return;
            }
            this.enrolledStudentsLoading = true;
            try {
                const result = await this.$apollo.query({
                    query: gql`
                        query InternshipClinicalPlacementEnrolledStudents(
                            $token: String!
                            $first: Int!
                            $page: Int!
                        ) {
                            internshipClinicalPlacementEnrolledStudents(
                                token: $token
                                first: $first
                                page: $page
                            ) {
                                error
                                error_code
                                message
                                paginatorInfo {
                                    count
                                    currentPage
                                    firstItem
                                    hasMorePages
                                    lastItem
                                    lastPage
                                    perPage
                                    total
                                }
                                data {
                                    enrollment_id
                                    user_id
                                    status
                                    assignment_source
                                    student_id
                                    name_en
                                    eid
                                    entity
                                    campus_code
                                    program_name
                                    enrolled_at
                                    submitted_at
                                    events {
                                        id
                                        event_type
                                        note
                                        created_at
                                        user {
                                            id
                                            name
                                        }
                                    }
                                }
                            }
                        }
                    `,
                    variables: {
                        token: this.effectiveToken,
                        first: this.enrolledStudentsFirst,
                        page: this.enrolledStudentsPage,
                    },
                    fetchPolicy: "network-only",
                });
                const payload = result && result.data
                    && result.data.internshipClinicalPlacementEnrolledStudents;
                if (!payload || payload.error) {
                    this.resetEnrolledStudents();
                    return;
                }
                this.enrolledStudents = payload.data ? payload.data : [];
                this.enrolledStudentsPaginatorInfo = payload.paginatorInfo
                    ? payload.paginatorInfo
                    : null;
            } catch (e) {
                this.resetEnrolledStudents();
            } finally {
                this.enrolledStudentsLoading = false;
            }
        },
        formatEnrollmentStatus(status) {
            const s = status ? String(status) : "pending";
            if (s === "approved") {
                return "Approved";
            }
            if (s === "rejected") {
                return "Rejected";
            }
            return "Pending";
        },
        formatAssignmentSource(source) {
            const s = source ? String(source) : "student_request";
            if (s === "staff_assigned") {
                return "Staff assigned";
            }
            return "Student request";
        },
        assignmentSourceClass(source) {
            const s = source ? String(source) : "student_request";
            if (s === "staff_assigned") {
                return "iod-source-pill--staff";
            }
            return "iod-source-pill--student";
        },
        enrollmentStatusClass(status) {
            const s = status ? String(status) : "pending";
            if (s === "approved") {
                return "iod-status-pill--approved";
            }
            if (s === "rejected") {
                return "iod-status-pill--rejected";
            }
            return "iod-status-pill--pending";
        },
        enrollmentStatusChipClass(status) {
            const s = status ? String(status) : "pending";
            if (s === "approved") {
                return "iod-chip-enrolled";
            }
            if (s === "rejected") {
                return "iod-chip-rejected";
            }
            return "iod-chip-pending";
        },
        viewStudentProfile(studentId) {
            if (!studentId) {
                return;
            }
            this.studentId = studentId;
            this.showStudentProfileFullModal = true;
        },
        closeStudentProfileModal() {
            this.showStudentProfileFullModal = false;
            this.studentId = null;
        },
        async approveEnrollment(student) {
            if (!this.canReviewEnrollments || !student || !student.enrollment_id || this.reviewSubmitting) {
                return;
            }
            this.reviewSubmitting = true;
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation ApproveInternshipClinicalPlacementEnrollment($enrollmentId: ID!) {
                            approveInternshipClinicalPlacementEnrollment(enrollment_id: $enrollmentId) {
                                error
                                message
                            }
                        }
                    `,
                    variables: { enrollmentId: String(student.enrollment_id) },
                });
                const payload = result && result.data
                    && result.data.approveInternshipClinicalPlacementEnrollment;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : "Could not approve enrollment.";
                    this.notifyToast("error", msg);
                    return;
                }
                this.notifyToast("success", payload.message || "Enrollment approved.");
                await this.fetchEnrolledStudents();
            } catch (e) {
                this.notifyToast("error", "Could not approve enrollment.");
            } finally {
                this.reviewSubmitting = false;
            }
        },
        async rejectEnrollment(student) {
            if (!this.canReviewEnrollments || !student || !student.enrollment_id || this.reviewSubmitting) {
                return;
            }
            if (!this.$swal) {
                this.notifyToast("error", "Unable to open rejection dialog.");
                return;
            }
            const swalResult = await this.$swal.fire({
                title: "Reject enrollment",
                text: "Please provide a reason for rejection. The student will be able to read and reply.",
                input: "textarea",
                inputPlaceholder: "Rejection reason (required)",
                inputAttributes: {
                    "aria-label": "Rejection reason",
                },
                showCancelButton: true,
                confirmButtonText: "Reject",
                confirmButtonColor: "#dc2626",
                inputValidator: function (value) {
                    if (!value || !String(value).trim()) {
                        return "A rejection reason is required.";
                    }
                    return null;
                },
            });
            if (!swalResult.isConfirmed) {
                return;
            }
            const reason = swalResult.value ? String(swalResult.value).trim() : "";
            if (!reason) {
                return;
            }
            this.reviewSubmitting = true;
            try {
                const result = await this.$apollo.mutate({
                    mutation: gql`
                        mutation RejectInternshipClinicalPlacementEnrollment($enrollmentId: ID!, $reason: String!) {
                            rejectInternshipClinicalPlacementEnrollment(enrollment_id: $enrollmentId, reason: $reason) {
                                error
                                message
                            }
                        }
                    `,
                    variables: {
                        enrollmentId: String(student.enrollment_id),
                        reason: reason,
                    },
                });
                const payload = result && result.data
                    && result.data.rejectInternshipClinicalPlacementEnrollment;
                if (!payload || payload.error) {
                    const msg = payload && payload.message ? payload.message : "Could not reject enrollment.";
                    this.notifyToast("error", msg);
                    return;
                }
                this.notifyToast("success", payload.message || "Enrollment rejected.");
                await this.fetchEnrolledStudents();
            } catch (e) {
                this.notifyToast("error", "Could not reject enrollment.");
            } finally {
                this.reviewSubmitting = false;
            }
        },
    },
};
</script>

<style scoped>
.iod-back-link {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: #1d4ed8;
    text-decoration: none;
    margin-bottom: 16px;
    transition: color 150ms ease;
}

.iod-back-link:hover {
    color: #1e3a8a;
    text-decoration: underline;
}

.iod-back-link-inline {
    margin-top: 12px;
    margin-bottom: 0;
}

.iod-hero {
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    border-radius: 20px;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 12px 32px rgba(15, 23, 42, 0.06);
    margin-bottom: 20px;
}

.iod-hero-stripe {
    width: 5px;
    flex-shrink: 0;
    background: linear-gradient(180deg, #3b82f6 0%, #1d4ed8 55%, #1e3a8a 100%);
}

.iod-hero-inner {
    flex: 1;
    min-width: 0;
    padding: 22px 22px 20px 18px;
}

.iod-hero-top {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 14px;
}

.iod-hero-leading {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    min-width: 0;
}

.iod-icon-tile {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: linear-gradient(145deg, #eff6ff 0%, #dbeafe 100%);
    border: 1px solid #bfdbfe;
    color: #1d4ed8;
    flex-shrink: 0;
}

.iod-icon-svg {
    width: 24px;
    height: 24px;
}

.iod-chip {
    display: inline-flex;
    align-items: center;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    padding: 5px 11px;
    border: 1px solid transparent;
    max-width: 100%;
}

.iod-chip-type {
    background: #ecfdf5;
    border-color: #a7f3d0;
    color: #047857;
}

.iod-chip-entity {
    background: #f8fafc;
    border-color: #e2e8f0;
    color: #334155;
    flex-shrink: 0;
}

.iod-title {
    margin: 0;
    font-size: 1.65rem;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: -0.02em;
    color: #0f172a;
}

.iod-panel {
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    background: #ffffff;
    padding: 22px 24px;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}

.iod-panel-muted {
    color: #64748b;
}

.iod-panel-error {
    border-color: #fecaca;
    background: #fef2f2;
}

.iod-panel-body {
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04), 0 8px 24px rgba(15, 23, 42, 0.05);
}

.iod-section-title {
    margin: 0 0 14px 0;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
}

.iod-section-title-spaced {
    margin-top: 28px;
}

.iod-dl {
    margin: 0;
}

.iod-dl-row {
    display: grid;
    grid-template-columns: 140px 1fr;
    gap: 12px 20px;
    padding: 10px 0;
    border-bottom: 1px solid #f1f5f9;
}

.iod-dl-row:last-of-type {
    border-bottom: none;
}

@media (max-width: 520px) {
    .iod-dl-row {
        grid-template-columns: 1fr;
    }
}

.iod-dt {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: #94a3b8;
}

.iod-dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    word-break: break-word;
}

.iod-desc-panel {
    padding: 16px 18px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
}

.iod-desc-text {
    margin: 0;
    font-size: 14px;
    line-height: 1.65;
    color: #475569;
    white-space: pre-wrap;
}

.iod-desc-muted {
    color: #94a3b8;
    font-style: italic;
}

.iod-enroll-wrap {
    margin-top: 8px;
}

.iod-enroll-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    padding: 14px 16px;
    border-radius: 14px;
    background: #f0fdf4;
    border: 1px solid #bbf7d0;
}

.iod-enroll-bar--rejected {
    background: #fff7ed;
    border-color: #fed7aa;
}

.iod-chip-enrolled {
    background: #ecfdf5;
    border: 1px solid #6ee7b7;
    color: #047857;
    font-weight: 700;
    font-size: 12px;
    padding: 6px 14px;
}

.iod-btn-enroll {
    cursor: pointer;
    border-radius: 10px;
    padding: 10px 22px;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(180deg, #10b981 0%, #059669 100%);
    border: none;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(16, 185, 129, 0.35);
    transition: background 150ms ease, box-shadow 150ms ease, transform 150ms ease, opacity 150ms ease;
}

.iod-btn-enroll:hover:not(:disabled) {
    background: linear-gradient(180deg, #059669 0%, #047857 100%);
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.1), 0 8px 20px rgba(5, 150, 105, 0.35);
    transform: translateY(-1px);
}

.iod-btn-enroll:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.iod-btn-resubmit {
    cursor: pointer;
    border-radius: 10px;
    padding: 10px 22px;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(37, 99, 235, 0.35);
    transition: background 150ms ease, box-shadow 150ms ease, transform 150ms ease, opacity 150ms ease;
}

.iod-btn-resubmit:hover:not(:disabled) {
    background: linear-gradient(180deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 2px 4px rgba(15, 23, 42, 0.1), 0 8px 20px rgba(29, 78, 216, 0.35);
    transform: translateY(-1px);
}

.iod-btn-resubmit:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.iod-resubmit-hint {
    margin-top: 12px;
    margin-bottom: 0;
    padding: 12px 14px;
    border-radius: 12px;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    color: #9a3412;
    font-size: 13px;
    line-height: 1.5;
}

.iod-enrolled-wrap {
    margin-top: 8px;
}

.iod-enrolled-loading {
    display: flex;
    justify-content: center;
    padding: 24px 0;
}

.iod-enrolled-table-wrap {
    border: 1px solid #f1f5f9;
    border-radius: 14px;
    padding: 12px 14px;
    background: #f8fafc;
}

.iod-enrolled-empty {
    padding: 16px 18px;
    border-radius: 14px;
    background: #f8fafc;
    border: 1px solid #f1f5f9;
    font-size: 14px;
    color: #64748b;
}

.iod-root--embedded .iod-hero {
    margin-bottom: 16px;
}

.iod-status-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    text-transform: capitalize;
}

.iod-source-pill {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 9999px;
    font-size: 11px;
    font-weight: 600;
    white-space: nowrap;
}

.iod-source-pill--staff {
    background: #eff6ff;
    color: #1d4ed8;
    border: 1px solid #bfdbfe;
}

.iod-source-pill--student {
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
}

.iod-resubmit-cell {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.iod-resubmit-count {
    font-weight: 600;
    color: #0f172a;
}

.iod-resubmit-last {
    font-size: 11px;
    color: #64748b;
    white-space: nowrap;
}

.iod-history-type {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
}

.iod-history-type--submitted {
    color: #334155;
}

.iod-history-type--rejected {
    color: #b91c1c;
}

.iod-history-type--resubmitted {
    color: #1d4ed8;
}

.iod-history-type--approved {
    color: #047857;
}

.iod-history-time {
    font-size: 11px;
    color: #64748b;
    white-space: nowrap;
}

.iod-history-timeline {
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 4px 2px 8px;
}

.iod-history-item {
    display: flex;
    gap: 12px;
    position: relative;
    padding-bottom: 16px;
}

.iod-history-item:last-child {
    padding-bottom: 0;
}

.iod-history-item:not(:last-child)::before {
    content: "";
    position: absolute;
    left: 5px;
    top: 14px;
    bottom: 0;
    width: 2px;
    background: #e2e8f0;
}

.iod-history-item-dot {
    width: 12px;
    height: 12px;
    border-radius: 9999px;
    margin-top: 3px;
    flex-shrink: 0;
    background: #94a3b8;
    border: 2px solid #ffffff;
    box-shadow: 0 0 0 1px #cbd5e1;
    z-index: 1;
}

.iod-history-item-dot.iod-history-type--submitted {
    background: #64748b;
    box-shadow: 0 0 0 1px #94a3b8;
}

.iod-history-item-dot.iod-history-type--rejected {
    background: #ef4444;
    box-shadow: 0 0 0 1px #fca5a5;
}

.iod-history-item-dot.iod-history-type--resubmitted {
    background: #2563eb;
    box-shadow: 0 0 0 1px #93c5fd;
}

.iod-history-item-dot.iod-history-type--approved {
    background: #10b981;
    box-shadow: 0 0 0 1px #6ee7b7;
}

.iod-history-item-body {
    min-width: 0;
    flex: 1;
}

.iod-history-item-top {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px 12px;
}

.iod-history-actor {
    margin: 4px 0 0;
    font-size: 12px;
    color: #64748b;
}

.iod-history-note {
    margin: 8px 0 0;
    padding: 10px 12px;
    border-radius: 10px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    font-size: 13px;
    line-height: 1.45;
    color: #334155;
    white-space: pre-wrap;
    word-break: break-word;
}

.icl-history-btn {
    cursor: pointer;
    border-radius: 8px;
    padding: 7px 12px;
    font-size: 12px;
    font-weight: 600;
    color: #475569;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    transition: background 150ms ease, border-color 150ms ease, color 150ms ease;
}

.icl-history-btn:hover {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #0f172a;
}

.icl-modal-header-icon-wrap--history {
    background: #f1f5f9;
    color: #475569;
}

.iod-status-pill--pending {
    background: #fef3c7;
    color: #92400e;
}

.iod-status-pill--approved {
    background: #dcfce7;
    color: #166534;
}

.iod-status-pill--rejected {
    background: #fee2e2;
    color: #991b1b;
}

.iod-chip-pending {
    background: #fef3c7;
    color: #92400e;
    border: 1px solid #fde68a;
}

.iod-chip-rejected {
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
}

.iod-btn-action {
    font-size: 12px;
    font-weight: 600;
    padding: 4px 10px;
    border-radius: 8px;
    border: 1px solid transparent;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease;
}

.iod-btn-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.iod-btn-action--approve {
    background: #dcfce7;
    color: #166534;
    border-color: #bbf7d0;
}

.iod-btn-action--approve:hover:not(:disabled) {
    background: #bbf7d0;
}

.iod-btn-action--reject {
    background: #fee2e2;
    color: #991b1b;
    border-color: #fecaca;
}

.iod-btn-action--reject:hover:not(:disabled) {
    background: #fecaca;
}

.icl-chat-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.625rem;
    cursor: pointer;
    box-shadow: 0 4px 14px rgba(37, 99, 235, 0.3);
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.icl-chat-btn:hover {
    background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
    box-shadow: 0 6px 20px rgba(37, 99, 235, 0.4);
    transform: translateY(-1px);
}

.icl-chat-btn-icon-wrap {
    width: 1.5rem;
    height: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
    background: rgba(255, 255, 255, 0.2);
}

.icl-chat-btn-icon {
    font-size: 0.75rem;
}

.icl-chat-btn-label {
    letter-spacing: 0.01em;
}

.icl-modal-header {
    padding: 1.25rem 3rem 1.25rem 1.5rem;
    background: linear-gradient(135deg, #0f172a 0%, #1e3a8a 45%, #2563eb 100%);
    color: #fff;
    border-radius: 1rem 1rem 0 0;
    box-shadow: inset 0 -1px 0 rgba(255, 255, 255, 0.08);
}

.icl-modal-header-inner {
    display: flex;
    align-items: center;
    gap: 0.875rem;
}

.icl-modal-header-icon-wrap {
    width: 2.75rem;
    height: 2.75rem;
    border-radius: 0.75rem;
    background: rgba(255, 255, 255, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.icl-modal-header-icon {
    font-size: 1.35rem;
    opacity: 0.95;
}

.icl-modal-header-title {
    font-size: 1.125rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    margin: 0;
    line-height: 1.3;
}

.icl-modal-header-sub {
    margin: 0.2rem 0 0 0;
    font-size: 0.8125rem;
    color: rgba(219, 234, 254, 0.95);
    line-height: 1.35;
}

.icl-modal-header-meta {
    opacity: 0.85;
}

.icl-modal-body {
    /* base-modal reserves 2rem below the slot; pull it back so the panel background reaches the rounded edge */
    margin-bottom: -2rem;
    padding: 1.5rem 1.5rem 3.5rem 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.icl-modal-card {
    background: #fff;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px rgba(15, 23, 42, 0.08), 0 2px 8px rgba(15, 23, 42, 0.04);
    overflow: hidden;
}

.icl-modal-hint {
    margin: 1.25rem 0 0 0;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    color: #64748b;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 0.75rem;
    line-height: 1.45;
}

.icl-modal-hint-icon {
    margin-right: 0.25rem;
    color: #f59e0b;
}

.icl-fade-enter-active,
.icl-fade-leave-active {
    transition: opacity 0.2s ease;
}

.icl-fade-enter,
.icl-fade-leave-to {
    opacity: 0;
}
</style>
